// trace-capture-chrome — the isolated "trace-capture" surface (trace-table-capture, an
// AUTHORING convenience for the Trace Table dialog). Host chrome AROUND the unchanged
// runner: the teacher's reference program runs on the read-only client-runner (READ-ONLY
// source beside it), and this chrome drives ONE instrumented per-line trace-capture pass
// (editor-core requestTraceCapture → the additive 0.17.0 traceCapture load option) to
// auto-discover the module-level variables + the execution-order trace, then hands them
// back to the course-editor dialog over a same-origin BroadcastChannel ('pc-trace-capture'
// — COOP severs window.opener for the isolated page).
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
//
// This is NOT a student surface: no prediction gate, no respond POST, no grading. The
// capture runs once on ready; a wall-clock timeout (S1a) stops a runaway program so the
// tab never spins forever. NO runner-contract change beyond the additive traceCapture pass.

// ~15s wall-clock bound on the whole capture (S1a). The trace-capture pass installs an
// EOF-raising stdin and caps rows/events, so a well-behaved console program terminates
// quickly; this backstop covers a program that never yields (e.g. a hot C-level loop that
// emits no module line events) — we stop() it and hand back whatever the dialog can use.
var CAPTURE_TIMEOUT_MS = 15000;

// Read + parse the server-embedded capture config (already '<'-escaped by the handler).
function readConfig(doc) {
  var el = doc.getElementById('trace-capture-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    if (!cfg || cfg.capture !== true) { return null; }
    return cfg;
  } catch (e) { return null; }
}

export function wire(ctx) {
  var doc  = ctx.doc;
  var core = ctx.core;
  var cfg  = readConfig(doc);
  if (!cfg) { return; }

  var panel = doc.getElementById('tracecap-panel');
  if (!panel) { return; }

  // The trace-capture pass is console Python-only (matches trace-table v1). A non-python
  // config is a misuse — surface it plainly and never run.
  var isPython = cfg.lang === 'python';

  var settled = false;   // a result (or the timeout) has been handled — once only
  var started = false;   // the capture run has been kicked off (on ready)
  var timer   = null;

  // ── DOM ─────────────────────────────────────────────────────────────────────
  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }

  panel.appendChild(el('h2', 'tracecap-heading', 'Capture the trace'));
  var sub = el('p', 'tracecap-sub', isPython
    ? 'Running the program to discover its variables and record the step-by-step trace…'
    : 'Trace capture works with console Python programs only.');
  panel.appendChild(sub);

  var summary = el('div', 'tracecap-summary');
  summary.hidden = true;
  panel.appendChild(summary);

  var actions = el('div', 'tracecap-actions');
  var useBtn = el('button', 'tracecap-btn tracecap-btn-use', 'Use these values');
  useBtn.type = 'button';
  useBtn.disabled = true;
  var cancelBtn = el('button', 'tracecap-btn tracecap-btn-cancel', 'Cancel');
  cancelBtn.type = 'button';
  actions.appendChild(useBtn);
  actions.appendChild(cancelBtn);
  panel.appendChild(actions);

  // The captured result handed back to the dialog (X: { variables, rows, truncated, error }).
  var captured = { variables: [], rows: [], truncated: false, error: null };

  function clearTimer() {
    if (timer != null) { doc.defaultView.clearTimeout(timer); timer = null; }
  }

  // ── finish: render the result + enable handoff (once) ─────────────────────────
  function finish(result) {
    if (settled) { return; }
    settled = true;
    clearTimer();

    captured.variables = (result && Array.isArray(result.variables)) ? result.variables : [];
    captured.rows = (result && Array.isArray(result.rows)) ? result.rows : [];
    captured.truncated = !!(result && result.truncated);
    captured.error = (result && result.error) || null;

    var nVars = captured.variables.length;
    var nRows = captured.rows.length;

    if (captured.error) {
      sub.textContent = captured.error;
      sub.classList.add('is-error');
    } else {
      sub.textContent = 'Done. Review what was found, then send it to the dialog.';
      sub.classList.remove('is-error');
    }

    if (nVars || nRows) {
      summary.textContent = '';
      summary.appendChild(el('div', null,
        'Found ' + nVars + ' variable' + (nVars === 1 ? '' : 's') +
        ' and ' + nRows + ' step' + (nRows === 1 ? '' : 's') + '.'));
      if (nVars) {
        summary.appendChild(el('div', 'tracecap-vars', captured.variables.join(', ')));
      }
      if (captured.truncated) {
        summary.appendChild(el('div', null, 'This is a long trace — the dialog shows the first steps.'));
      }
      summary.hidden = false;
      useBtn.disabled = false;
    } else {
      // No trackable variables / no trace — nothing to send. The dialog also guards
      // this, but keep the handoff disabled so the teacher cancels back.
      summary.textContent = 'No trackable variables were found — close this and fill the grid by hand.';
      summary.hidden = false;
      useBtn.disabled = true;
    }
  }

  // ── handoff / cancel ──────────────────────────────────────────────────────────
  useBtn.addEventListener('click', function () {
    if (useBtn.disabled) { return; }
    try {
      var bc = new BroadcastChannel('pc-trace-capture');
      bc.postMessage({
        variables: captured.variables,
        rows: captured.rows,
        truncated: captured.truncated,
        error: captured.error
      });
      bc.close();
    } catch (e) { /* no BroadcastChannel — the teacher fills the grid by hand */ }
    try { doc.defaultView.close(); } catch (e) { /* ignore */ }
  });
  cancelBtn.addEventListener('click', function () {
    try { doc.defaultView.close(); } catch (e) { /* ignore */ }
  });

  if (!isPython) { return; } // never run a non-python capture

  // ── drive the capture ─────────────────────────────────────────────────────────
  function startCapture() {
    if (started) { return; }
    started = true;
    // Wall-clock backstop (S1a): stop a runaway run and hand back an error so the tab
    // never spins. The dialog's own 15s listener timeout (X3) is NOT sufficient — it only
    // abandons the channel and would leave this tab spinning.
    timer = doc.defaultView.setTimeout(function () {
      timer = null;
      try { core.stop(); } catch (e) { /* ignore */ }
      finish({ variables: [], rows: [], truncated: true,
        error: 'The program took too long to finish, so the capture stopped. ' +
               'Try a shorter program, or fill the grid by hand.' });
    }, CAPTURE_TIMEOUT_MS);

    core.requestTraceCapture().then(function (result) { finish(result); });
  }

  // Belt-and-braces (S2): the pass installs EOF stdin so input() raises EOFError and never
  // parks — but if the runner ever emits an input-request, stop the run so we don't hang.
  core.on('input-request', function () {
    if (!settled) { try { core.stop(); } catch (e) { /* ignore */ } }
  });

  // A runner-level failure (harness install fatal, isolation error) leaves
  // requestTraceCapture's promise pending — the wall-clock timeout covers it, but resolve
  // sooner on a terminal runner-error state so the teacher isn't left waiting 15s.
  core.on('state', function (state) {
    if (!started || settled) { return; }
    if (state === 'runner-error') {
      finish({ variables: [], rows: [], truncated: false,
        error: 'The runner could not run the program. Try again, or fill the grid by hand.' });
    }
  });

  // Kick off the capture once the runner reports ready. The listener is registered
  // synchronously here, before the async runner-ready fires, so there is no missed-event
  // race (matches the predict-chrome capture pattern).
  core.on('ready', startCapture);
}
